<template>
    <div class="min-h-screen bg-slate-50 dark:bg-slate-900">
        <!-- Header -->
        <header class="fixed w-full bg-white dark:bg-slate-800 border-b border-slate-900/10 dark:border-slate-50/[0.06] z-50">
            <!-- Primary Navigation Menu -->
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex justify-between xxxh-16">
                    <!-- Logo and Navigation Container -->
                    <div class="w-full flex flex-col">
                        <!-- Logo and Mobile Menu Button Row -->
                        <div class="flex justify-between items-center h-12">
                            <!-- Logo and desktop navigation container -->
                            <div class="flex items-center">
                                <!-- Logo -->
                                <Link :href="$page.props.urls.home" class="flex items-center">
                                    <img class="h-6 dark:hidden" :src="asset('/images/logo.png')" alt="logo" loading="eager"/>
                                    <img class="hidden h-6 dark:block" :src="asset('/images/logo-white.png')" alt="logo" loading="eager"/>
                                    <span class="ml-3 text-xl xxxfont-semibold xxxxtext-slate-900 dark:text-white orbitron-400">{{ currentLocale === 'nl' ? 'Powerprompt' : 'GetPowerprompts' }}
                                    </span>
                                </Link>

                                <!-- Desktop Navigation Links - on same row as logo -->
<!--                                <div class="hidden sm:flex ml-10 space-x-8 orbitron">-->
<!--                                    <TopMenu v-if="$page?.props?.mainCategories" :mainCategories="$page.props.mainCategories"/>-->
<!--                                </div>-->
                            </div>
                            <!-- Header Tools -->
                            <div class="flex items-center">
                                <!-- HeaderIcons - only visible on desktop -->
                                <div class="hidden sm:block">
                                    <HeaderIcons/>
                                </div>
                                <!-- Language Switcher - only visible on desktop -->
                                <div class="hidden sm:block">
                                    <LanguageSwitcher class="mr-2"/>
                                </div>

                                <!-- User Menu - only visible on desktop -->
                                <template v-if="$page.props.auth?.user">
                                    <div class="hidden sm:block ml-3 relative">
                                        <Dropdown align="right" width="48">
                                            <template #trigger>
                                                <span class="inline-flex rounded-md">
                                                    <button type="button"
                                                            class="inline-flex items-center px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 hover:border-slate-300 focus:outline-none focus:text-slate-700 focus:border-slate-300 transition duration-150 ease-in-out cursor-pointer">
                                                        {{ $page.props.auth.user.name }}
                                                        <ChevronDownIcon class="ml-2 -mr-0.5 h-4 w-4"/>
                                                    </button>
                                                </span>
                                            </template>

                                            <template #content>
                                                <DropdownLink :href="$page.props.urls.profile"
                                                              class="block w-full px-4 py-2 text-sm leading-5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                                                    {{ $t('my_profile') }}
                                                </DropdownLink>
                                                <DropdownLink :href="$page.props.urls.logout" method="post" as="button"
                                                              class="block w-full px-4 py-2 text-sm leading-5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                                                    {{ $t('logout') }}
                                                </DropdownLink>
                                            </template>
                                        </Dropdown>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="flex items-center gap-x-2">
                                        <!-- Login link - only visible on desktop -->
                                        <Link :href="$page.props.urls.login"
                                              :data="{ intended: $page.url }"
                                              method="get"
                                              class="hidden sm:inline-flex text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300">
                                            {{ $t('login') }}
                                        </Link>
                                    </div>
                                </template>

                                <!-- Mobile Menu Button -->
                                <div class="-mr-2 flex items-center sm:hidden">
                                    <button @click="showingNavigationDropdown = !showingNavigationDropdown"
                                            class="inline-flex items-center justify-center p-2 rounded-md text-slate-400 hover:text-slate-500 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 focus:text-slate-500 transition duration-150 ease-in-out">
                                        <MenuIcon v-if="!showingNavigationDropdown" class="h-6 w-6"/>
                                        <XIcon v-else class="h-6 w-6"/>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="pb-2 flex items-center">
                            <div class="w-1/5 text-left whitespace-nowrap" ><i class="hidden sm:block">{{ $t('slogan') }}</i></div>
                            <div class="w-3/5 flex justify-center">
                                <PromptSearch @update:selected="handlePromptSelected" />
                            </div>
                            <div class="w-1/5"></div> <!-- Empty div for balance -->
                        </div>

                        <!-- Mobile Navigation Links - below logo -->
                        <!--div class="sm:hidden pb-2 w-full overflow-x-auto">
                            <TopMenu v-if="$page?.props?.mainCategories" :mainCategories="$page.props.mainCategories"/>
                        </div-->
                    </div>
                </div>
            </div>

            <!-- Mobile Navigation Menu -->
            <div :class="{'block': showingNavigationDropdown, 'hidden': !showingNavigationDropdown}" class="sm:hidden bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-lg">
                <div class="pt-2 pb-3 space-y-1">

                    <!-- Mobile user menu -->
                    <div v-if="$page.props.auth?.user" class="px-2 py-3 border-t border-slate-200 dark:border-slate-700">
                        <div class="flex items-center px-3 py-2">
                            <span class="text-base font-medium text-slate-800 dark:text-white">
                                {{ $page.props.auth.user.name }}
                            </span>
                        </div>
                        <Link
                            :href="$page.props.urls.profile"
                            class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md mb-2"
                        >
                            {{ $t('my_profile') }}
                        </Link>
                        <Link
                            :href="$page.props.urls.logout"
                            method="post"
                            as="button"
                            class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md"
                        >
                            {{ $t('logout') }}
                        </Link>
                    </div>

                    <!-- Mobile-only tools for guests -->
                    <div v-if="!$page.props.auth?.user" class="px-2 py-3 border-t border-slate-200 dark:border-slate-700">
                        <!-- Login link in mobile menu -->
                        <Link :href="$page.props.urls.login"
                              :data="{ intended: $page.url }"
                              method="get"
                              class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md mb-2">
                            {{ $t('login') }}
                        </Link>

                        <!-- Register link in mobile menu -->
                        <Link :href="$page.props.urls.register"
                              :data="{ intended: $page.url }"
                              method="get"
                              class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md">
                            {{ $t('register') }}
                        </Link>
                    </div>

                    <!-- Language switcher in mobile menu -->
                    <div class="px-2 py-3 border-t border-slate-200 dark:border-slate-700">
                        <div class="px-3">
                            <LanguageSwitcher/>
                        </div>
                    </div>

                    <!-- Dark mode toggle in mobile menu -->
                    <div class="px-2 py-3 border-t border-slate-200 dark:border-slate-700">
                        <div class="px-3">
                            <HeaderIcons/>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <!-- Main Content -->
        <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-32" xxxstyle="background:green;">

            <div v-if="$page.props.title === 'IT Prompts'" class="w-full flex flex-wrap mb-12">
                <!-- Left part (50%) -->
                <div class="w-full md:w-1/2 flex items-start">
                    <div class="p-4">
                        <h1 class="text-2xl font-bold mb-4">{{ $t('homeTitle') }}</h1>
                        {{ $t('homeIntro') }}
                        <!-- Additional content can be added here -->
                    </div>
                </div>
                <!-- Right part (50%) with image -->
                <div class="w-full md:w-1/2 flex justify-end items-start">
                    <img :src="asset('/images/screen.jpg')" alt="Screen" class="w-2/4 h-auto" />
                </div>
            </div>

            <!-- Admin Recalculate Icon Button - Only visible for admin users -->
            <div v-if="$page.props.auth?.user?.admin === 1" class="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div class="flex items-center justify-between">
                    <div class="text-sm text-blue-800 dark:text-blue-300">
                        <strong>Admin Tools:</strong> Use this button to recalculate all icons for the current page.
                    </div>
                    <div v-if="$page.props.pageCategory && $page.props[$page.props.pageCategory]?.id" class="text-sm text-slate-500 dark:text-slate-400">
                        <span>category id: {{ $page.props[$page.props.pageCategory].id }} ({{ $page.props[$page.props.pageCategory].name }})</span>
                    </div>
                    <a
                        :href="usePage().url.includes('recalculate=true') ? currentPath : currentPath + '?recalculate=true'"
                        class="px-4 py-2 text-sm font-bold bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
                    >
                        <span class="mr-2">🔄</span> {{ usePage().url.includes('recalculate=true') ? 'Do not recalculate Icons' : 'Recalculate Icons' }}
                    </a>
                </div>
            </div>

            <!-- Page Header -->
            <div v-if="$page.props.title !== 'IT Prompts'" >
                <div class="flex items-start justify-between">
                    <div>
                        <h2 class="text-2xl font-semibold text-slate-900 dark:text-white">
                            {{ $t($page.props.title) }}
                        </h2>
                        <nav v-if="$page.props.breadcrumbs" class="mt-2 mb-6" aria-label="Breadcrumb">
                            <ol class="flex items-center space-x-0 sm:space-x-2 xxxoverflow-x-auto">
                                <!-- Process each breadcrumb item -->
                                <template v-for="(item, index) in $page.props.breadcrumbs" :key="index">
                                    <!-- Skip the last item on prompt pages -->
                                    <template v-if="!(isPromptPage && index === $page.props.breadcrumbs.length - 1)">
                                        <!-- Item container with conditional display for mobile -->
                                        <li class="flex items-center flex-shrink-0"
                                            :class="{
                                                'hidden sm:flex': $page.props.breadcrumbs.length > 3 && index < 2,
                                                'flex-1': isLastVisibleItem(index) && $page.props.breadcrumbs.length > 1
                                            }">

                                            <!-- Add separator before items (except the first one) -->
                                            <template v-if="index > 0">
                                                <!-- For mobile with more than 3 items, if this is the first visible item (index=2), don't show the separator on mobile -->
                                                <template v-if="$page.props.breadcrumbs.length > 3 && index === 2">
                                                    <ChevronRightIcon class="hidden sm:block mx-2 h-5 w-5 text-slate-400"/>
                                                </template>
                                                <template v-else>
                                                    <ChevronRightIcon class="mx-1 sm:mx-2 h-4 sm:h-5 w-4 sm:w-5 text-slate-400"/>
                                                </template>
                                            </template>

                                            <!-- Item content -->
                                            <Link
                                                v-if="item.url || item.href"
                                                :href="item.url || item.href"
                                                class="text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                                            >
                                                <!-- On desktop, show full text -->
                                                <span class="hidden sm:inline">{{ $t(item.name) }}</span>

                                                <!-- On mobile: For the last visible item, truncate if too long; for others, just show without pipeline prefix -->
                                                <span
                                                    class="sm:hidden"
                                                    :class="{
                                                        'truncate block w-full': isLastVisibleItem(index),
                                                    }"
                                                >
                                                    {{ formatMobileCategoryName($t(item.name)) }}
                                                </span>
                                            </Link>
                                            <span
                                                v-else
                                                class="text-sm font-medium text-slate-700 dark:text-white"
                                            >
                                                <!-- On desktop, show full text -->
                                                <span class="hidden sm:inline">{{ $t(item.name) }}</span>

                                                <!-- On mobile: For the last visible item, truncate if too long; for others, just show without pipeline prefix -->
                                                <span
                                                    class="sm:hidden"
                                                    :class="{
                                                        'truncate block w-full': isLastVisibleItem(index),
                                                    }"
                                                >
                                                    {{ formatMobileCategoryName($t(item.name)) }}
                                                </span>
                                            </span>
                                        </li>
                                    </template>
                                </template>
                            </ol>
                        </nav>
                    </div>

                    <div class="hidden sm:flex items-start justify-center sm:justify-start">
                        <div
                            v-if="page.props.icon"
                            class="h-12 w-12 sm:h-14 sm:w-14 flex items-center justify-center bg-blue-600 rounded-full p-1 sm:p-2"
                            :title="page.props.icon?.componentName"
                            data-tooltip-placement="top"
                        >
                            <component
                                v-if="iconComponent"
                                :is="iconComponent"
                                class="h-10 w-10 sm:h-12 sm:w-12 text-white"
                                :stroke-width="2.5"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Page Content -->

            <slot></slot>
        </main>

        <!-- Footer -->
        <footer class="fixed bottom-0 w-full border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
            <div class="max-w-7xl mx-auto px-4 py-6">
                <div class="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
                    <p class="text-sm text-slate-500 dark:text-slate-400">
                        {{ $t('copyright_text') }}
                    </p>
                    <nav class="flex space-x-4 text-sm">
                        <a href="#" class="text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-500">{{ $t('about_us') }}</a>
                        <a href="#" class="text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-500">{{ $t('privacy') }}</a>
                        <a href="#" class="text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-500">{{ $t('terms') }}</a>
                        <a href="#" class="text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-500">{{ $t('contact') }}</a>
                    </nav>
                </div>
            </div>
        </footer>
    </div>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue'
import {Link, usePage, router} from '@inertiajs/vue3'
import {asset} from '@/helpers'
import Dropdown from '@/Components/Dropdown.vue'
import DropdownLink from '@/Components/DropdownLink.vue'
import HeaderIcons from '@/Components/Icons/HeaderIcons.vue'
import TopMenu from '@/Components/Navigation/TopMenu.vue'
import {ChevronDownIcon, ChevronRightIcon, MenuIcon, XIcon} from '@/Components/Icons'
import {getIcon} from '@/Components/Icons/IconRegistry'
import LanguageSwitcher from '@/Components/Navigation/LanguageSwitcher.vue'
import PromptSearch from '@/Components/Prompts/PromptSearch.vue'
import {Clock} from 'lucide-vue-next';

const showingNavigationDropdown = ref(false)

// Get the page object from Inertia
const page = usePage()

// Ref for the icon component
const iconComponent = ref(null)

// Function to format category names for mobile by removing the part before the pipeline
const formatMobileCategoryName = (name) => {
    if (name && name.includes(' | ')) {
        return name.split(' | ').pop();
    }
    return name;
}

// Function to determine if an item is the last visible breadcrumb on mobile
const isLastVisibleItem = (index) => {
    // If we're on a prompt page, the last item is hidden
    if (isPromptPage.value && index === page.props.breadcrumbs.length - 1) {
        return false;
    }

    // If we have more than 3 items and this is not the last visible one
    if (page.props.breadcrumbs.length > 3) {
        // For items that are hidden on mobile (first 2 when more than 3 items)
        if (index < 2) {
            return false;
        }

        // For the last visible item (either the last item or the second-to-last on prompt pages)
        return isPromptPage.value
            ? index === page.props.breadcrumbs.length - 2
            : index === page.props.breadcrumbs.length - 1;
    }

    // For 3 or fewer items, the last one is the last visible (or second-to-last on prompt pages)
    return isPromptPage.value
        ? index === page.props.breadcrumbs.length - 2
        : index === page.props.breadcrumbs.length - 1;
}

// Load the icon component when the component is mounted
onMounted(async () => {
    // Debug: Log all page props to see what's available
    console.log('All page props:', page.props)
    if (page.props.icon && page.props.icon.componentName) {
        iconComponent.value = await getIcon(page.props.icon.componentName)
    }
})

// State for admin controls visibility
const isAdmin = computed(() => {
    const page = usePage();
    return page.props.auth &&
           page.props.auth.user &&
           page.props.auth.user.admin === 1;
})

// Check if we're on a prompt page
const isPromptPage = computed(() => {
    return page.props.prompt?.id !== undefined;
})

// Function to get the appropriate URL for recalculating icons based on the current page
const getRecalculateIconUrl = () => {
    // For category pages
    if (page.props.category?.id) {
        return `/categories/${page.props.category.id}/recalculate-icon`;
    }
    // For subcategory pages
    else if (page.props.subcategory?.id) {
        return `/categories/${page.props.subcategory.id}/recalculate-icon`;
    }
    // For subsubcategory pages
    else if (page.props.subsubcategory?.id) {
        return `/categories/${page.props.subsubcategory.id}/recalculate-icon`;
    }
    // For prompt pages
    else if (page.props.prompt?.id) {
        // For prompt pages, we need to use the subsubcategory ID instead of the prompt ID
        if (page.props.subsubcategory?.id) {
            return `/categories/${page.props.subsubcategory.id}/recalculate-icon`;
        }
        // Fallback to subcategory if available
        else if (page.props.subcategory?.id) {
            return `/categories/${page.props.subcategory.id}/recalculate-icon`;
        }
        // Fallback to category if available
        else if (page.props.category?.id) {
            return `/categories/${page.props.category.id}/recalculate-icon`;
        }
    }
    // Default fallback
    return '/admin/recalculate-icons';
}

// No form submission needed for simple link approach

// Computed property for current path
const currentPath = computed(() => {
    return usePage().url.split('?')[0]; // Get the path without query parameters
})

// Check if recalculate parameter is active in the URL
const isRecalculateActive = computed(() => {
    return usePage().url.includes('recalculate=true');
})

// Function to handle prompt selection from the search component
const handlePromptSelected = (prompt) => {
    if (!prompt) return;

    // If the prompt has a URL, use Inertia to navigate to it without a full page reload
    if (prompt.url) {
        router.visit(prompt.url, {
            preserveScroll: false,  // Don't preserve scroll position
            preserveState: false,   // Don't preserve component state
        });
    } else if (prompt.id) {
        console.log('Selected prompt without URL, ID:', prompt.id);
    }
};

const currentLocale = computed(() => {
    return usePage().props.locale || this.$i18n.locale;
});
</script>

<style>
.grid {
    gap: 1.5rem; /* This is equivalent to gap-6 */
}
</style>
